<template>
  <div
    class="relative w-full select-none overflow-hidden"
    ref="wrapperRef"
    @wheel="onWheel"
    @mousedown="onPointerDown"
    @mousemove="onPointerMove"
    @mouseup="onPointerUp"
    @mouseleave="onWrapperLeave"
    @touchstart.prevent="onTouchstart"
    @touchmove.prevent="onTouchmove"
    @touchend.prevent="onTouchend"
  >
    <div
      ref="transformWrapper"
      :style="transformStyle"
      style="transform-origin: 0 0; will-change: transform; position: relative;"
    >
      <!-- Low-res full-seatmap fallback (always visible, provides layout flow) -->
      <canvas
        ref="canvasFallback"
        class="block w-full h-auto"
        style="position: relative; z-index: 1;"
      />
      <!-- Invisible interaction layer (full seatmap, 1×) -->
      <canvas
        ref="canvasInteraction"
        class="block w-full h-auto"
        style="position: absolute; top: 0; left: 0; z-index: 2; cursor: crosshair;"
      />
    </div>
    <!-- High-res viewport bake overlay — outside transform to avoid CSS scale blur.
         Sized dynamically (up to 300%) to provide pre-rendered buffer for smooth panning.
         On iOS the buffer is reduced to stay within the 16MP canvas pixel budget.
         A live CSS transform compensates pan/zoom delta (GPU-composited). -->
    <canvas
      ref="canvasBase"
      class="block"
      :style="[overlayPositionStyle, overlayTransformStyle, { zIndex: 3, opacity: overlayOpacity, transition: 'opacity 150ms ease' }]"
    />
    <canvas
      ref="canvasHighlight"
      class="block"
      :style="[overlayPositionStyle, overlayTransformStyle, { zIndex: 4, opacity: overlayOpacity, transition: 'opacity 150ms ease' }]"
    />
    <!-- Stop touch events reaching the wrapper, whose touchstart.prevent would suppress the button click -->
    <div
      class="flex absolute bottom-7 left-7 z-10 gap-3 touch-none"
      @mousedown.stop
      @mouseup.stop
      @mousemove.stop
      @touchstart.stop
      @touchmove.stop
      @touchend.stop
    >
      <button
        type="button"
        class="cursor-pointer"
        aria-label="Zoom out"
        @click.stop="zoomOut"
      >
        <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M24 0.5C36.9787 0.5 47.5 11.0213 47.5 24C47.5 36.9787 36.9787 47.5 24 47.5C11.0213 47.5 0.5 36.9787 0.5 24C0.5 11.0213 11.0213 0.5 24 0.5Z" fill="white" stroke="#E7E7E7"/>
          <path d="M22.8478 25.1521H16.3198V22.8481H22.8478H25.1518H31.6798V25.1521H25.1518H22.8478Z" fill="#171717"/>
        </svg>
      </button>
      <button
        type="button"
        class="cursor-pointer"
        aria-label="Zoom in"
        @click.stop="zoomIn"
      >
        <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M24 0.5C36.9787 0.5 47.5 11.0213 47.5 24C47.5 36.9787 36.9787 47.5 24 47.5C11.0213 47.5 0.5 36.9787 0.5 24C0.5 11.0213 11.0213 0.5 24 0.5Z" fill="white" stroke="#E7E7E7"/>
          <path d="M22.8478 25.1521H16.3198V22.8481H22.8478V16.3201H25.1518V22.8481H31.6798V25.1521H25.1518V31.6801H22.8478V25.1521Z" fill="#171717"/>
        </svg>
      </button>
    </div>
  </div>
  <Teleport to="body">
    <div
      v-if="tooltipVisible"
      class="pointer-events-none fixed z-50 rounded bg-black/80 px-2 py-1 text-sm text-white whitespace-nowrap"
      :style="{ left: tooltipX + 'px', top: tooltipY + 'px' }"
    >
      {{ tooltipText }}
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import { useSeatmapStore } from '@/stores/seatmapStore';
import { useToastStore } from '@/stores/toastStore';
import type { SeatmapData, SeatPriceGroupData } from '@/types';
import { canvasCoords, drawSeatImage, getTouchDist } from '@/utils/seatmap/canvas/canvasHelpers';
import { buildSeatColorArray } from '@/utils/seatmap/canvas/priceGroupColors';
import { seatIconSvg, selectedSeatIconSvg, svgToImage } from '@/utils/seatmap/canvas/seatIcons';
import { findClosestFreeSeat, getSeatLabel, seatsOursIncludes } from '@/utils/seatmap/canvas/seatUtils';

// ─── Props ────────────────────────────────────────────────────────────────────

const props = defineProps<{
  name: string;
  count: number;
}>();

// ─── Constants ────────────────────────────────────────────────────────────────

// How much larger (multiplier) the pre-rendered bake canvas is vs. the visible area.
// Used in getVisibleSeatmapRect() and bakeAtCurrentZoom() to provide off-screen buffer for smooth panning.
const VIEWPORT_BUFFER = 3;
// Extra padding (in seatmap coordinate units) added when culling seats during bake/highlight draws,
// so seats partially at the viewport edge are still rendered.
const SEAT_MARGIN = 60;
// Maximum zoom level allowed. Enforced in applyZoom() and pinch handlers.
const ZOOM_MAX = 10;
// Debounce delay (ms) before triggering a hi-DPI bake after zoom/pan settles. Used in scheduleBake().
const BAKE_DEBOUNCE_MS = 250;

// iOS Safari has much stricter canvas pixel limits (~16 MP) than desktop/Android (~268 MP).
// Exceeding the limit causes the canvas to silently render blank.
const IS_IOS = /iPad|iPhone|iPod/.test(navigator.userAgent)
  || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1); // iPadOS
const MAX_CANVAS_PIXELS = IS_IOS ? 16_777_216 : 268_000_000;

// ─── Store & reactive state ───────────────────────────────────────────────────

const { t } = useI18n();
const seatmapStore = useSeatmapStore();
const toastStore = useToastStore();

// DOM refs — the seatmap uses a multi-canvas architecture:
// canvasFallback: low-res full-seatmap (always visible, provides CSS layout sizing)
// canvasBase: hi-res viewport-baked overlay showing sharp seats at current zoom
// canvasHighlight: overlay for the currently hovered/blinking seat
// canvasInteraction: invisible layer that receives pointer events for hit-testing
const wrapperRef = ref<HTMLDivElement | null>(null);
const canvasFallback = ref<HTMLCanvasElement | null>(null);
const canvasBase = ref<HTMLCanvasElement | null>(null);
const canvasHighlight = ref<HTMLCanvasElement | null>(null);
const canvasInteraction = ref<HTMLCanvasElement | null>(null);
const transformWrapper = ref<HTMLDivElement | null>(null);

// Highlight & blink state — tracks which seat is hovered and drives the blink animation
let highlightedSeatIndex = -1; // index into seatmapData.seats, -1 = none
let blinkInterval: ReturnType<typeof setInterval> | null = null;
let blinkVisible = true;
let lastMouseClientX = -1; // cached for rehighlightAtLastMouse() after zoom
let lastMouseClientY = -1;

// Tooltip state
const tooltipVisible = ref(false);
const tooltipText = ref('');
const tooltipX = ref(0);
const tooltipY = ref(0);

// Per-seattype cached HTMLImageElement sets — Key: "<typeIndex>-<colorHex>-<bakedZoom>"
const imageCache = new Map<string, HTMLImageElement>();

// Set to true once initCanvas has finished; guards the seat-state watcher.
let canvasReady = false;

// On mobile, lock the wrapper height after the first calculation so
// scroll-triggered resize events (address bar hide/show) don't cause jumps.
let mobileLockedHeight: number | null = null;
let lastKnownWidth: number = 0;

// Zoom & pan state — drives the CSS transform on transformWrapper
const zoomMin = ref(1); // computed in applyFitToViewport() so the map always fits
const zoom = ref(1);
const panX = ref(0); // CSS pixels offset from top-left
const panY = ref(0);

// Mouse drag panning state (onPointerDown/Move/Up)
let isPanning = false;
let panStartClientX = 0;
let panStartClientY = 0;
let panOriginX = 0;
let panOriginY = 0;
let hasPanned = false; // distinguishes drag from click

// Double-click detection state (desktop)
let lastClickTime = 0;
let lastClickX = 0;
let lastClickY = 0;

// Touch pinch-zoom state (onTouchstart/move/end)
let pinchStartDist = 0; // initial finger distance
let pinchStartZoom = 1;
let pinchContentAnchorX = 0; // content-space anchor for pinch zoom
let pinchContentAnchorY = 0;
let lastTouchMidX = 0;
let lastTouchMidY = 0;
let touchStartX = 0; // single-finger pan start
let touchStartY = 0;
let panTouchOriginX = 0;
let panTouchOriginY = 0;

// Double-tap detection state (mobile) — mirrors desktop double-click zoom
let lastTapTime = 0;
let lastTapX = 0;
let lastTapY = 0;
let tapTimeout: ReturnType<typeof setTimeout> | null = null;

// Hi-DPI bake state — after zoom settles, the canvas backing buffer is
// re-rendered at a higher resolution ("bakedZoom") so seats stay sharp.
const bakedZoom = ref(1);
let bakeTimeout: ReturnType<typeof setTimeout> | null = null;
let bakeGeneration = 0;
let cachedBackgroundImage: HTMLImageElement | null = null;

interface ViewRect { left: number; top: number; right: number; bottom: number; }
let bakedViewRect: ViewRect | null = null;

// Track the pan/zoom state at which the overlay was last baked.
const bakedPanX = ref(0);
const bakedPanY = ref(0);
const bakedZoomLevel = ref(1);

// Effective buffer multiplier — may be reduced from VIEWPORT_BUFFER on iOS to
// stay within the 16MP canvas pixel budget while preserving full DPR sharpness.
const effectiveBufferRef = ref(VIEWPORT_BUFFER);

// Controls overlay visibility during resize transitions.
const overlayOpacity = ref(1);

// Resize tracking
let resizeTimeout: ReturnType<typeof setTimeout> | null = null;
let wrapperResizeObserver: ResizeObserver | null = null;
let lastWrapperWidth: number = 0;
let lastWrapperHeight: number = 0;

// ─── Computed styles ──────────────────────────────────────────────────────────

// CSS transform applied to the inner wrapper div (contains fallback + interaction canvases).
const transformStyle = computed(() => ({
  transform: `translate(${panX.value}px, ${panY.value}px) scale(${zoom.value})`,
}));

// Positions the hi-res overlay canvases with padding around the viewport for buffer.
const overlayPositionStyle = computed(() => {
  const buf = effectiveBufferRef.value;
  const offset = (buf - 1) / 2 * 100;
  return {
    position: 'absolute' as const,
    left: `-${offset}%`,
    top: `-${offset}%`,
    width: `${buf * 100}%`,
    height: `${buf * 100}%`,
    pointerEvents: 'none' as const,
    willChange: 'transform' as const,
  };
});

// Live GPU-composited transform that compensates pan/zoom delta since last bake,
// keeping the overlay aligned until a fresh bake catches up.
const overlayTransformStyle = computed(() => {
  const ratio = zoom.value / bakedZoomLevel.value;
  const tx = panX.value - bakedPanX.value * ratio;
  const ty = panY.value - bakedPanY.value * ratio;
  const buf = effectiveBufferRef.value;
  const originPct = ((buf - 1) / 2 / buf * 100).toFixed(4);
  const origin = `${originPct}% ${originPct}%`;
  if (Math.abs(tx) < 0.01 && Math.abs(ty) < 0.01 && Math.abs(ratio - 1) < 0.001) {
    return { transform: 'none', transformOrigin: origin };
  }
  return {
    transform: `translate(${tx}px, ${ty}px) scale(${ratio})`,
    transformOrigin: origin,
  };
});

// ─── Store accessors ──────────────────────────────────────────────────────────

/** Returns the static seatmap layout data (seats, types, background) for the current seatmap name. */
function getSeatmapData(): SeatmapData | undefined {
  return seatmapStore.cachedSeatmaps[props.name];
}

/** Returns the live seat reservation state (which seats are taken/ours) for the current seatmap. */
function getSeatState() {
  return seatmapStore.seatStates[props.name] ?? { state2: [], seatsOurs: [] };
}

// ─── Image loading ────────────────────────────────────────────────────────────

/** Load (or retrieve from cache) an image for a given seattype index and colour. */
async function getSeattypeImage(seatmapData: SeatmapData, typeIndex: number, colorHex: string, bake: number = bakedZoom.value): Promise<HTMLImageElement> {
  const cacheKey = `${typeIndex}-${colorHex}-${bake}`;
  const cached = imageCache.get(cacheKey);
  if (cached) return cached;

  const seatType = seatmapData.seatTypes[typeIndex];
  const z = seatType?.zoom ?? 1;
  const baseSize = 24;
  const size = Math.round(baseSize * z * 21.25 * bake);

  const svg = colorHex === '__selected__' ? selectedSeatIconSvg(size) : seatIconSvg(colorHex, size);
  const image = await svgToImage(svg, size, size);
  imageCache.set(cacheKey, image);
  return image;
}

// ─── Canvas sizing ────────────────────────────────────────────────────────────

/** Sets the fallback canvas pixel dimensions to match the background image, scaled by DPR (capped to avoid iOS limits). */
function syncFallbackSize(backgroundImage: HTMLImageElement) {
  const c = canvasFallback.value;
  if (!c) return;
  const natW = backgroundImage.naturalWidth;
  const natH = backgroundImage.naturalHeight;
  let dpr = window.devicePixelRatio || 1;
  const maxDpr = Math.sqrt(MAX_CANVAS_PIXELS / (natW * natH));
  if (dpr > maxDpr) dpr = Math.max(1, maxDpr);
  c.width = natW * dpr;
  c.height = natH * dpr;
  c.style.width = '100%';
  c.style.height = 'auto';
  const ctx = c.getContext('2d');
  if (ctx) ctx.scale(dpr, dpr);
}

/** Sets the invisible interaction canvas size to match the background image for accurate hit-testing of seat positions. */
function syncInteractionSize(backgroundImage: HTMLImageElement) {
  const ci = canvasInteraction.value;
  if (!ci) return;
  const natW = backgroundImage.naturalWidth;
  const natH = backgroundImage.naturalHeight;
  const interactionDpr = Math.min(window.devicePixelRatio || 1, Math.sqrt(MAX_CANVAS_PIXELS / (natW * natH)));
  ci.width = natW * interactionDpr;
  ci.height = natH * interactionDpr;
  ci.style.width = '100%';
  ci.style.height = 'auto';
  const ctx = ci.getContext('2d');
  if (ctx) ctx.scale(interactionDpr, interactionDpr);
}

// ─── Viewport calculation ─────────────────────────────────────────────────────

/** Computes the currently visible region of the seatmap (in seatmap units), expanded by VIEWPORT_BUFFER padding. Used by bakeAtCurrentZoom() to cull off-screen seats. */
function getVisibleSeatmapRect(): ViewRect {
  const wrapper = wrapperRef.value;
  const bg = cachedBackgroundImage;
  if (!wrapper || !bg) return { left: 0, top: 0, right: bg?.naturalWidth ?? 1000, bottom: bg?.naturalHeight ?? 1000 };

  const ww = wrapper.clientWidth;
  const wh = wrapper.clientHeight;
  const natW = bg.naturalWidth;
  const natH = bg.naturalHeight;

  const seatmapPerCSS = natW / ww;
  const visLeft = -panX.value / zoom.value * seatmapPerCSS;
  const visTop = -panY.value / zoom.value * seatmapPerCSS;
  const visRight = visLeft + (ww / zoom.value) * seatmapPerCSS;
  const visBottom = visTop + (wh / zoom.value) * seatmapPerCSS;

  const visW = visRight - visLeft;
  const visH = visBottom - visTop;
  const padW = visW * (VIEWPORT_BUFFER - 1) / 2;
  const padH = visH * (VIEWPORT_BUFFER - 1) / 2;

  return {
    left: Math.max(0, visLeft - padW),
    top: Math.max(0, visTop - padH),
    right: Math.min(natW, visRight + padW),
    bottom: Math.min(natH, visBottom + padH),
  };
}

// ─── Drawing ──────────────────────────────────────────────────────────────────

/** Draws the seatmap background image onto the low-res fallback canvas. Called during initCanvas(). */
async function drawBackground(backgroundImage: HTMLImageElement) {
  const canvas = canvasFallback.value;
  if (!canvas) return;
  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  ctx.clearRect(0, 0, backgroundImage.naturalWidth, backgroundImage.naturalHeight);
  ctx.drawImage(backgroundImage, 0, 0, backgroundImage.naturalWidth, backgroundImage.naturalHeight);
}

/** Redraws ALL seats on the low-res fallback canvas with correct colours based on state (taken/free/ours/override). Called on init and whenever seat state changes via WebSocket. */
async function drawAllSeats(seatmapData: SeatmapData, state2: number[], seatsOurs: number[], colors: number[], overrideColors: string[], priceGroups?: Record<string, Record<string, SeatPriceGroupData>>) {
  const canvas = canvasFallback.value;
  if (!canvas) return;
  const ctx = canvas.getContext('2d');
  if (!ctx) return;

  // Use cached background image; fall back to loading if not yet cached
  let backgroundImage = cachedBackgroundImage;
  if (!backgroundImage) {
    backgroundImage = new Image();
    backgroundImage.src = seatmapData.backgroundUrl ?? '';
    await new Promise<void>(res => {
      if (backgroundImage!.complete && backgroundImage!.naturalWidth > 0) { res(); return; }
      backgroundImage!.onload = () => res();
      backgroundImage!.onerror = () => res();
    });
    cachedBackgroundImage = backgroundImage;
  }
  ctx.clearRect(0, 0, backgroundImage.naturalWidth, backgroundImage.naturalHeight);
  if (backgroundImage.naturalWidth > 0) {
    ctx.drawImage(backgroundImage, 0, 0, backgroundImage.naturalWidth, backgroundImage.naturalHeight);
  }

  // Build price-group colour map for free-seat colouring (single pass over all groups)
  const pgSeatColors = buildSeatColorArray(priceGroups);

  for (let i = 0; i < seatmapData.seats.length; i++) {
    const seat = seatmapData.seats[i];
    let colorHex: string;
    const seatState2 = state2[i] ?? 0;
    const seatColorIdx = colors[i] ?? 0;
    // 1. seatsOurs takes priority: draw as black circle with green checkmark
    if (seatsOursIncludes(seatsOurs, i)) {
      colorHex = '__selected__';
    // 2. Taken seat whose color group matches the override index → override color
    } else if (seatState2 > 0 && seatColorIdx > 0 && seatColorIdx === seatState2 && overrideColors[seatColorIdx]) {
      colorHex = overrideColors[seatColorIdx];
    // 3. Taken seat with no matching override → normal taken color
    } else if (seatState2 > 0) {
      colorHex = seatmapData.render.colorTaken;
    // 4. Free seat with a zone override → override color
    } else if (seatColorIdx > 0 && overrideColors[seatColorIdx]) {
      colorHex = overrideColors[seatColorIdx];
    // 5. Free seat in a price group → price-group colour
    } else {
      colorHex = pgSeatColors[i] ?? seatmapData.render.colorFree;
    }
    const image = await getSeattypeImage(seatmapData, seat.seatTypeIndex ?? 0, colorHex, 1);
    drawSeatImage(ctx, seat, image, 1);
  }
}

/** Draws a single highlighted seat on the hi-res highlight overlay canvas. Used when hovering over or selecting a seat. */
async function drawHighlightSeat(seatmapData: SeatmapData, seatIndex: number) {
  const canvas = canvasHighlight.value;
  if (!canvas || !bakedViewRect || !cachedBackgroundImage || !wrapperRef.value) return;
  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  ctx.save();
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.clearRect(0, 0, canvas.width, canvas.height);
  ctx.restore();
  if (seatIndex < 0 || seatIndex >= seatmapData.seats.length) return;

  const seat = seatmapData.seats[seatIndex];
  if (seat.x < bakedViewRect.left - SEAT_MARGIN || seat.x > bakedViewRect.right + SEAT_MARGIN
    || seat.y < bakedViewRect.top - SEAT_MARGIN || seat.y > bakedViewRect.bottom + SEAT_MARGIN) return;

  const dpr = window.devicePixelRatio || 1;
  const natW = cachedBackgroundImage.naturalWidth;
  const ww = wrapperRef.value.clientWidth;
  const wh = wrapperRef.value.clientHeight;
  const screenScale = (ww / natW) * bakedZoomLevel.value * dpr;
  const padX = (effectiveBufferRef.value - 1) / 2 * ww * dpr;
  const padY = (effectiveBufferRef.value - 1) / 2 * wh * dpr;
  const offsetX = bakedPanX.value * dpr + padX;
  const offsetY = bakedPanY.value * dpr + padY;

  ctx.save();
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.translate(offsetX, offsetY);
  ctx.scale(screenScale, screenScale);
  const effectiveBake = bakedZoom.value || 1;
  const image = await getSeattypeImage(seatmapData, seat.seatTypeIndex ?? 0, seatmapData.render.colorHighlighted, effectiveBake);
  drawSeatImage(ctx, seat, image, effectiveBake);
  ctx.restore();
}

/** Clears the highlight overlay canvas. Called when the cursor moves away from a seat or after a click. */
function clearHighlight() {
  const canvas = canvasHighlight.value;
  if (!canvas) return;
  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  ctx.save();
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.clearRect(0, 0, canvas.width, canvas.height);
  ctx.restore();
}

// ─── Visual feedback ──────────────────────────────────────────────────────────

/** Starts a 400ms on/off blink animation on the highlight canvas to draw attention to the hovered seat. */
function startBlink() {
  stopBlink();
  blinkVisible = true;
  if (canvasHighlight.value) canvasHighlight.value.style.opacity = '1';
  blinkInterval = setInterval(() => {
    blinkVisible = !blinkVisible;
    if (canvasHighlight.value) {
      canvasHighlight.value.style.opacity = blinkVisible ? '1' : '0';
    }
  }, 400);
}

/** Stops the blink animation and resets the highlight canvas to fully visible. */
function stopBlink() {
  if (blinkInterval !== null) {
    clearInterval(blinkInterval);
    blinkInterval = null;
  }
  if (canvasHighlight.value) canvasHighlight.value.style.opacity = '1';
}

/** Positions and displays the floating tooltip (row/seat label) near the cursor for the given seat. */
function showTooltip(seatmapData: SeatmapData, seatIndex: number, clientX: number, clientY: number) {
  const seat = seatmapData.seats[seatIndex];
  if (!seat) return;
  tooltipText.value = getSeatLabel(seatmapData, seat);
  tooltipX.value = clientX + 12;
  tooltipY.value = clientY - 36;
  tooltipVisible.value = true;
}

/** Hides the seat label tooltip. Called on mouse leave or when no seat is under the cursor. */
function hideTooltip() {
  tooltipVisible.value = false;
}

// ─── Zoom & pan ───────────────────────────────────────────────────────────────

/** Constrains panX/panY so the seatmap cannot be dragged outside the wrapper bounds. Centers it if smaller. */
function clampPan() {
  const wrapper = wrapperRef.value;
  const canvas = canvasFallback.value;
  if (!wrapper || !canvas) return;
  const ww = wrapper.clientWidth;
  const wh = wrapper.clientHeight;
  const cw = canvas.offsetWidth;
  const ch = canvas.offsetHeight;
  if (!cw || !ch) return;
  const scaledCw = cw * zoom.value;
  const scaledCh = ch * zoom.value;
  panX.value = scaledCw <= ww
    ? (ww - scaledCw) / 2
    : Math.min(0, Math.max(ww - scaledCw, panX.value));
  panY.value = scaledCh <= wh
    ? (wh - scaledCh) / 2
    : Math.min(0, Math.max(wh - scaledCh, panY.value));
}

/** Applies a new zoom level while keeping the given pivot point (in wrapper-local pixels) visually stationary. Triggers a bake and re-highlight. */
function applyZoom(newZoom: number, pivotX: number, pivotY: number) {
  const clamped = Math.min(ZOOM_MAX, Math.max(zoomMin.value, newZoom));
  const pivotContentX = (pivotX - panX.value) / zoom.value;
  const pivotContentY = (pivotY - panY.value) / zoom.value;
  panX.value = pivotX - pivotContentX * clamped;
  panY.value = pivotY - pivotContentY * clamped;
  zoom.value = clamped;
  clampPan();
  scheduleBake();
  nextTick().then(rehighlightAtLastMouse);
}

/** Zooms in by 30% around the wrapper center. Bound to the "+" button. */
function zoomIn() {
  const wrapper = wrapperRef.value;
  if (!wrapper) return;
  applyZoom(zoom.value * 1.3, wrapper.clientWidth / 2, wrapper.clientHeight / 2);
}

/** Zooms out by 30% around the wrapper center. Bound to the "−" button. */
function zoomOut() {
  const wrapper = wrapperRef.value;
  if (!wrapper) return;
  applyZoom(zoom.value / 1.3, wrapper.clientWidth / 2, wrapper.clientHeight / 2);
}

// ─── Interaction handlers ─────────────────────────────────────────────────────

/** Handles Ctrl+scroll wheel zoom — zooms toward the cursor position. */
function onWheel(e: WheelEvent) {
  if (!e.ctrlKey) return;
  e.preventDefault();
  const rect = wrapperRef.value!.getBoundingClientRect();
  applyZoom(
    zoom.value * (e.deltaY < 0 ? 1.15 : 1 / 1.15),
    e.clientX - rect.left,
    e.clientY - rect.top,
  );
}

/** Begins a potential pan or click on left mouse button down. Stores origin for drag detection. */
function onPointerDown(e: MouseEvent) {
  if (e.button !== 0) return;
  isPanning = true;
  hasPanned = false;
  panStartClientX = e.clientX;
  panStartClientY = e.clientY;
  panOriginX = panX.value;
  panOriginY = panY.value;
  if (canvasInteraction.value) canvasInteraction.value.style.cursor = 'grabbing';
}

/** During a drag: pans the seatmap. Otherwise delegates to onMousemove for seat hover detection. */
function onPointerMove(e: MouseEvent) {
  if (isPanning) {
    const dx = e.clientX - panStartClientX;
    const dy = e.clientY - panStartClientY;
    panX.value = panOriginX + dx;
    panY.value = panOriginY + dy;
    clampPan();
    if (Math.abs(dx) > 5 || Math.abs(dy) > 5) {
      hasPanned = true;
      clearHighlight();
      stopBlink();
      hideTooltip();
      highlightedSeatIndex = -1;
      scheduleBakeIfNeeded();
    }
  } else {
    onMousemove(e);
  }
}

/** On mouse up: if the user dragged, finalize pan. Otherwise detect single-click (select seat) or double-click (zoom). */
function onPointerUp(e: MouseEvent) {
  if (!isPanning) return;
  isPanning = false;
  if (canvasInteraction.value) canvasInteraction.value.style.cursor = 'crosshair';
  if (hasPanned) {
    lastClickTime = 0;
    return;
  }
  const now = Date.now();
  const dt = now - lastClickTime;
  const dx = Math.abs(e.clientX - lastClickX);
  const dy = Math.abs(e.clientY - lastClickY);
  if (dt < 300 && dx < 5 && dy < 5) {
    // Double-click detected — zoom if cursor is not over a seat
    lastClickTime = 0;
    const canvas = canvasInteraction.value;
    const seatmapData = getSeatmapData();
    if (canvas && seatmapData) {
      const { x, y } = canvasCoords(canvas, e.clientX, e.clientY);
      const { state2, seatsOurs } = getSeatState();
      if (findClosestFreeSeat(seatmapData, x, y, state2, seatsOurs) < 0) {
        const rect = wrapperRef.value!.getBoundingClientRect();
        const factor = e.shiftKey ? 1 / 1.3 : 1.3;
        applyZoom(zoom.value * factor, e.clientX - rect.left, e.clientY - rect.top);
      }
    } else if (highlightedSeatIndex < 0) {
      const rect = wrapperRef.value!.getBoundingClientRect();
      const factor = e.shiftKey ? 1 / 1.3 : 1.3;
      applyZoom(zoom.value * factor, e.clientX - rect.left, e.clientY - rect.top);
    }
  } else {
    lastClickTime = now;
    lastClickX = e.clientX;
    lastClickY = e.clientY;
    performClick();
  }
}

/** Cancels any in-progress pan and clears hover state when the cursor leaves the wrapper. */
function onWrapperLeave() {
  if (isPanning) {
    isPanning = false;
    if (canvasInteraction.value) canvasInteraction.value.style.cursor = 'crosshair';
  }
  lastClickTime = 0;
  onMouseleave();
}

/** Touch start: initializes pinch-zoom (2 fingers) or single-finger pan state. */
function onTouchstart(e: TouchEvent) {
  hasPanned = false;
  if (e.touches.length === 2) {
    pinchStartDist = getTouchDist(e.touches);
    pinchStartZoom = zoom.value;
    const midX = (e.touches[0].clientX + e.touches[1].clientX) / 2;
    const midY = (e.touches[0].clientY + e.touches[1].clientY) / 2;
    lastTouchMidX = midX;
    lastTouchMidY = midY;
    const rect = wrapperRef.value!.getBoundingClientRect();
    pinchContentAnchorX = (midX - rect.left - panX.value) / zoom.value;
    pinchContentAnchorY = (midY - rect.top - panY.value) / zoom.value;
  } else if (e.touches.length === 1) {
    touchStartX = e.touches[0].clientX;
    touchStartY = e.touches[0].clientY;
    panTouchOriginX = panX.value;
    panTouchOriginY = panY.value;
  }
}

/** Touch move: applies pinch-zoom scaling (2 fingers) or single-finger panning. Requests bake if panned far enough. */
function onTouchmove(e: TouchEvent) {
  if (e.touches.length === 2) {
    const newDist = getTouchDist(e.touches);
    const newMidX = (e.touches[0].clientX + e.touches[1].clientX) / 2;
    const newMidY = (e.touches[0].clientY + e.touches[1].clientY) / 2;
    const rect = wrapperRef.value!.getBoundingClientRect();
    const newMidLocalX = newMidX - rect.left;
    const newMidLocalY = newMidY - rect.top;
    if (pinchStartDist > 0 && newDist > 0) {
      const newZoom = Math.min(ZOOM_MAX, Math.max(zoomMin.value, pinchStartZoom * (newDist / pinchStartDist)));
      panX.value = newMidLocalX - pinchContentAnchorX * newZoom;
      panY.value = newMidLocalY - pinchContentAnchorY * newZoom;
      zoom.value = newZoom;
      clampPan();
    } else {
      const oldMidLocalX = lastTouchMidX - rect.left;
      const oldMidLocalY = lastTouchMidY - rect.top;
      panX.value += newMidLocalX - oldMidLocalX;
      panY.value += newMidLocalY - oldMidLocalY;
      clampPan();
    }
    lastTouchMidX = newMidX;
    lastTouchMidY = newMidY;
    hasPanned = true;
    scheduleBakeIfNeeded();
  } else if (e.touches.length === 1) {
    const dx = e.touches[0].clientX - touchStartX;
    const dy = e.touches[0].clientY - touchStartY;
    if (Math.abs(dx) > 5 || Math.abs(dy) > 5) {
      hasPanned = true;
      panX.value = panTouchOriginX + dx;
      panY.value = panTouchOriginY + dy;
      clampPan();
      scheduleBakeIfNeeded();
    }
  }
}

/** Touch end: if not a pan, performs seat selection or double-tap zoom. Uses a 300ms delay to distinguish single vs double tap. */
function onTouchend(e: TouchEvent) {
  const canvas = canvasInteraction.value;
  const seatmapData = getSeatmapData();
  if (!canvas || !seatmapData) return;
  if (hasPanned) {
    if (e.touches.length == 0) {
      hasPanned = false;
    } else if (e.touches.length == 1) {
      touchStartX = e.touches[0].clientX;
      touchStartY = e.touches[0].clientY;
      panTouchOriginX = panX.value;
      panTouchOriginY = panY.value;
    }
    return;
  }
  const touch = e.changedTouches[0];
  if (!touch) return;
  const { x, y } = canvasCoords(canvas, touch.clientX, touch.clientY);
  const { state2, seatsOurs } = getSeatState();

  // Double-tap detection (mirrors desktop double-click zoom)
  const now = Date.now();
  const dt = now - lastTapTime;
  const dx = Math.abs(touch.clientX - lastTapX);
  const dy = Math.abs(touch.clientY - lastTapY);

  if (dt < 300 && dx < 30 && dy < 30) {
    lastTapTime = 0;
    if (tapTimeout !== null) { clearTimeout(tapTimeout); tapTimeout = null; }
    const seatIndex = findClosestFreeSeat(seatmapData, x, y, state2, seatsOurs);
    if (seatIndex < 0) {
      const rect = wrapperRef.value!.getBoundingClientRect();
      applyZoom(zoom.value * 1.3, touch.clientX - rect.left, touch.clientY - rect.top);
    } else {
      highlightedSeatIndex = seatIndex;
      performClick();
    }
    return;
  }

  // First tap — delay to allow double-tap detection
  lastTapTime = now;
  lastTapX = touch.clientX;
  lastTapY = touch.clientY;
  if (tapTimeout !== null) clearTimeout(tapTimeout);

  const seatIndex = findClosestFreeSeat(seatmapData, x, y, state2, seatsOurs);
  highlightedSeatIndex = seatIndex;
  tapTimeout = setTimeout(() => {
    tapTimeout = null;
    performClick();
  }, 300);
}

/** Desktop hover handler: finds the closest free seat under the cursor, highlights it, and shows tooltip. */
async function onMousemove(e: MouseEvent) {
  lastMouseClientX = e.clientX;
  lastMouseClientY = e.clientY;
  const canvas = canvasInteraction.value;
  const seatmapData = getSeatmapData();
  if (!canvas || !seatmapData) return;
  const { x, y } = canvasCoords(canvas, e.clientX, e.clientY);
  const { state2, seatsOurs } = getSeatState();
  const seatIndex = findClosestFreeSeat(seatmapData, x, y, state2, seatsOurs);
  if (seatIndex !== highlightedSeatIndex) {
    highlightedSeatIndex = seatIndex;
    if (seatIndex >= 0) {
      await drawHighlightSeat(seatmapData, seatIndex);
      startBlink();
    } else {
      clearHighlight();
      stopBlink();
    }
  }
  if (seatIndex >= 0) {
    showTooltip(seatmapData, seatIndex, e.clientX, e.clientY);
  } else {
    hideTooltip();
  }
}

/** Clears all hover/highlight state when the mouse leaves the seatmap area. */
function onMouseleave() {
  lastMouseClientX = -1;
  lastMouseClientY = -1;
  highlightedSeatIndex = -1;
  clearHighlight();
  stopBlink();
  hideTooltip();
}

/** Re-evaluates which seat is under the last known mouse position. Called after zoom changes to update the highlight. */
async function rehighlightAtLastMouse() {
  if (lastMouseClientX < 0 || lastMouseClientY < 0) return;
  const canvas = canvasInteraction.value;
  const seatmapData = getSeatmapData();
  if (!canvas || !seatmapData) return;
  const { x, y } = canvasCoords(canvas, lastMouseClientX, lastMouseClientY);
  const { state2, seatsOurs } = getSeatState();
  const seatIndex = findClosestFreeSeat(seatmapData, x, y, state2, seatsOurs);
  if (seatIndex === highlightedSeatIndex) return;
  highlightedSeatIndex = seatIndex;
  if (seatIndex >= 0) {
    await drawHighlightSeat(seatmapData, seatIndex);
    startBlink();
  } else {
    clearHighlight();
    stopBlink();
  }
}

/** Executes seat selection/deselection on the currently highlighted seat. Sends reservation via WebSocket. Shows toast if seat is taken. */
async function performClick() {
  const seatmapData = getSeatmapData();
  if (!seatmapData || highlightedSeatIndex < 0) return;
  const { state2, seatsOurs } = getSeatState();
  const seatIndex = highlightedSeatIndex;
  if ((state2[seatIndex] ?? 0) > 0 && !seatsOursIncludes(seatsOurs, seatIndex)) {
    toastStore.showToast(t('checkout.seat_taken'));
    return;
  }

  if (seatsOursIncludes(seatsOurs, seatIndex)) {
    const newSeats = seatsOurs.filter(s => Number(s) !== seatIndex);
    seatmapStore.sendReserveSeats(props.name, newSeats, newSeats.length === 0);
  } else {
    if (seatsOurs.length >= props.count) return;
    seatmapStore.validateAndSendReserveSeats(props.name, [...seatsOurs, seatIndex]);
  }

  highlightedSeatIndex = -1;
  clearHighlight();
  stopBlink();
}

// ─── Hi-DPI bake ──────────────────────────────────────────────────────────────

/** Renders the seatmap at full screen resolution into offscreen canvases, then swaps them onto the visible overlay.
 *  Only draws seats within the buffered viewport rect for performance. Uses bakeGeneration to cancel stale renders. */
async function bakeAtCurrentZoom() {
  if (!cachedBackgroundImage || !canvasReady) return;
  const seatmapData = getSeatmapData();
  if (!seatmapData) return;
  const wrapper = wrapperRef.value;
  if (!wrapper) return;

  const dpr = window.devicePixelRatio || 1;
  const natW = cachedBackgroundImage.naturalWidth;
  const natH = cachedBackgroundImage.naturalHeight;
  const ww = wrapper.clientWidth;
  const wh = wrapper.clientHeight;

  // Compute effective buffer that fits within the device's pixel budget at full DPR.
  let effectiveBuffer = VIEWPORT_BUFFER;
  const rawPixels = ww * VIEWPORT_BUFFER * dpr * wh * VIEWPORT_BUFFER * dpr;
  if (rawPixels > MAX_CANVAS_PIXELS) {
    effectiveBuffer = Math.sqrt(MAX_CANVAS_PIXELS * 0.95 / (ww * wh * dpr * dpr));
    effectiveBuffer = Math.max(1, effectiveBuffer);
  }
  effectiveBufferRef.value = effectiveBuffer;

  const canvasW = Math.ceil(ww * effectiveBuffer * dpr);
  const canvasH = Math.ceil(wh * effectiveBuffer * dpr);

  const vr = getVisibleSeatmapRect();
  const bufW = vr.right - vr.left;
  const bufH = vr.bottom - vr.top;
  if (bufW <= 0 || bufH <= 0) return;

  const unitToCss = ww / natW;
  const screenScale = unitToCss * zoom.value * dpr;
  const offsetX = panX.value * dpr;
  const offsetY = panY.value * dpr;

  const effectiveBake = Math.max(1, Math.round(screenScale * 10) / 10);
  const roundedBake = Math.round(effectiveBake * 10) / 10;

  const thisGeneration = ++bakeGeneration;

  // ── Build offscreen base canvas at screen resolution ─────────────────────
  const offBase = document.createElement('canvas');
  offBase.width = canvasW;
  offBase.height = canvasH;
  const offBaseCtx = offBase.getContext('2d');
  if (!offBaseCtx) return;

  const padX = (effectiveBuffer - 1) / 2 * ww * dpr;
  const padY = (effectiveBuffer - 1) / 2 * wh * dpr;
  offBaseCtx.translate(offsetX + padX, offsetY + padY);
  offBaseCtx.scale(screenScale, screenScale);

  offBaseCtx.drawImage(cachedBackgroundImage, 0, 0, natW, natH);

  // Draw only seats within the buffered viewport
  const { state2, seatsOurs, colors, overrideColors, priceGroups } = getSeatState();
  const pgSeatColors = buildSeatColorArray(priceGroups);
  const marginLeft = vr.left - SEAT_MARGIN;
  const marginTop = vr.top - SEAT_MARGIN;
  const marginRight = vr.right + SEAT_MARGIN;
  const marginBottom = vr.bottom + SEAT_MARGIN;

  for (let i = 0; i < seatmapData.seats.length; i++) {
    const seat = seatmapData.seats[i];
    if (seat.x < marginLeft || seat.x > marginRight || seat.y < marginTop || seat.y > marginBottom) continue;

    let colorHex: string;
    const seatState2 = state2[i] ?? 0;
    const seatColorIdx = colors[i] ?? 0;
    if (seatsOursIncludes(seatsOurs, i)) {
      colorHex = '__selected__';
    } else if (seatState2 > 0 && seatColorIdx > 0 && seatColorIdx === seatState2 && overrideColors[seatColorIdx]) {
      colorHex = overrideColors[seatColorIdx];
    } else if (seatState2 > 0) {
      colorHex = seatmapData.render.colorTaken;
    } else if (seatColorIdx > 0 && overrideColors[seatColorIdx]) {
      colorHex = overrideColors[seatColorIdx];
    } else {
      colorHex = pgSeatColors[i] ?? seatmapData.render.colorFree;
    }
    const image = await getSeattypeImage(seatmapData, seat.seatTypeIndex ?? 0, colorHex, effectiveBake);
    if (bakeGeneration !== thisGeneration) return;
    drawSeatImage(offBaseCtx, seat, image, effectiveBake);
  }

  if (bakeGeneration !== thisGeneration) return;

  // ── Build offscreen highlight canvas ─────────────────────────────────────
  let offHighlight: HTMLCanvasElement | null = null;
  if (highlightedSeatIndex >= 0 && highlightedSeatIndex < seatmapData.seats.length) {
    const hlSeat = seatmapData.seats[highlightedSeatIndex];
    if (hlSeat.x >= marginLeft && hlSeat.x <= marginRight && hlSeat.y >= marginTop && hlSeat.y <= marginBottom) {
      offHighlight = document.createElement('canvas');
      offHighlight.width = canvasW;
      offHighlight.height = canvasH;
      const offHlCtx = offHighlight.getContext('2d');
      if (offHlCtx) {
        offHlCtx.translate(offsetX + padX, offsetY + padY);
        offHlCtx.scale(screenScale, screenScale);
        const hlImage = await getSeattypeImage(seatmapData, hlSeat.seatTypeIndex ?? 0, seatmapData.render.colorHighlighted, effectiveBake);
        if (bakeGeneration !== thisGeneration) return;
        drawSeatImage(offHlCtx, hlSeat, hlImage, effectiveBake);
      }
    }
  }

  if (bakeGeneration !== thisGeneration) return;

  // ── Swap into visible canvases ───────────────────────────────────────────
  bakedZoom.value = roundedBake;
  bakedViewRect = { ...vr };
  bakedPanX.value = panX.value;
  bakedPanY.value = panY.value;
  bakedZoomLevel.value = zoom.value;

  const realBase = canvasBase.value;
  if (realBase) {
    realBase.width = canvasW;
    realBase.height = canvasH;
    const ctx = realBase.getContext('2d');
    if (ctx) ctx.drawImage(offBase, 0, 0);
  }

  const realHl = canvasHighlight.value;
  if (realHl) {
    realHl.width = canvasW;
    realHl.height = canvasH;
    const ctx = realHl.getContext('2d');
    if (ctx) {
      if (offHighlight) ctx.drawImage(offHighlight, 0, 0);
    }
  }

  overlayOpacity.value = 1;
}

/** Debounces a bake — called on every zoom/pan change to avoid excessive re-renders. */
function scheduleBake() {
  if (bakeTimeout !== null) clearTimeout(bakeTimeout);
  bakeTimeout = setTimeout(() => {
    bakeTimeout = null;
    bakeAtCurrentZoom();
  }, BAKE_DEBOUNCE_MS);
}

/** Only schedules a bake if the user has panned/zoomed far enough from the last baked state that the buffer edge is approaching. */
function scheduleBakeIfNeeded() {
  const ratio = zoom.value / bakedZoomLevel.value;
  const tx = Math.abs(panX.value - bakedPanX.value * ratio);
  const ty = Math.abs(panY.value - bakedPanY.value * ratio);
  const wrapper = wrapperRef.value;
  if (!wrapper) { scheduleBake(); return; }
  const buf = effectiveBufferRef.value;
  const thresholdX = (buf - 1) / 2 * wrapper.clientWidth * 0.7;
  const thresholdY = (buf - 1) / 2 * wrapper.clientHeight * 0.7;
  if (tx > thresholdX || ty > thresholdY || Math.abs(ratio - 1) > 0.01) {
    scheduleBake();
  }
}

// ─── Layout & resize ──────────────────────────────────────────────────────────

/** Calculates and applies the initial zoom/height so the seatmap fits within available viewport space (accounts for header + bottom bar). On mobile, locks height to prevent scroll-triggered jitter. */
function applyFitToViewport() {
  const wrapper = wrapperRef.value;
  const canvas = canvasFallback.value;
  if (!wrapper || !canvas) return;

  const viewportHeight = window.visualViewport?.height ?? window.innerHeight;
  // Use scroll-independent page position to avoid race with browser scroll restoration
  const wrapperTop = wrapper.getBoundingClientRect().top + window.scrollY;
  const isMobile = window.innerWidth < 768;
  const bottomReserve = isMobile ? 150 : 100;
  const availableHeight = viewportHeight - wrapperTop - bottomReserve;

  if (availableHeight <= 0) return;

  if (isMobile) {
    if (mobileLockedHeight == null) {
      mobileLockedHeight = availableHeight;
      lastKnownWidth = window.innerWidth;
    }
    const h = mobileLockedHeight;
    wrapper.style.height = `${h}px`;

    const scaleX = wrapper.clientWidth / canvas.offsetWidth;
    const scaleY = h / canvas.offsetHeight;
    const fitZoom = Math.min(scaleX, scaleY, 1);
    zoomMin.value = fitZoom;
    zoom.value = fitZoom;
    clampPan();
  } else {
    if (canvas.offsetHeight > availableHeight) {
      wrapper.style.height = `${availableHeight}px`;
      const fitZoom = availableHeight / canvas.offsetHeight;
      zoomMin.value = fitZoom;
      zoom.value = fitZoom;
      clampPan();
    }
  }
}

/** ResizeObserver callback: hides the overlay and schedules a re-bake when the wrapper dimensions change (e.g. sidebar toggle). */
function onWrapperResize(entries: ResizeObserverEntry[]) {
  const entry = entries[0];
  if (!entry) return;
  const { width, height } = entry.contentRect;
  if (Math.abs(width - lastWrapperWidth) < 2 && Math.abs(height - lastWrapperHeight) < 2) return;
  lastWrapperWidth = width;
  lastWrapperHeight = height;
  if (!canvasReady) return;
  overlayOpacity.value = 0;
  clampPan();
  scheduleBake();
}

/** Window resize handler (debounced 150ms): fully resets zoom/pan and re-initializes layout. On mobile, ignores vertical-only changes from address bar hide/show. */
function onResize() {
  if (resizeTimeout !== null) clearTimeout(resizeTimeout);
  resizeTimeout = setTimeout(async () => {
    const isMobile = window.innerWidth < 768;

    if (isMobile && mobileLockedHeight != null && Math.abs(window.innerWidth - lastKnownWidth) < 50) {
      return;
    }

    mobileLockedHeight = null;
    overlayOpacity.value = 0;

    const wrapper = wrapperRef.value;
    if (wrapper) wrapper.style.height = '';
    zoom.value = 1;
    zoomMin.value = 1;
    bakedZoom.value = 1;
    bakedViewRect = null;
    imageCache.clear();
    panX.value = 0;
    panY.value = 0;
    await nextTick();
    await document.fonts.ready;
    await new Promise<void>(r => requestAnimationFrame(() => requestAnimationFrame(() => r())));
    applyFitToViewport();
    scheduleBake();
  }, 150);
}

// ─── Lifecycle ────────────────────────────────────────────────────────────────

/** Main initialization: loads background image, sizes canvases, fits to viewport, draws all seats, then triggers first bake. */
async function initCanvas() {
  const seatmapData = getSeatmapData();
  if (!seatmapData?.backgroundUrl) return;

  const backgroundImage = new Image();
  backgroundImage.src = seatmapData.backgroundUrl;
  await new Promise<void>(res => {
    if (backgroundImage.complete && backgroundImage.naturalWidth > 0) { res(); return; }
    backgroundImage.onload = () => res();
    backgroundImage.onerror = () => res();
  });

  cachedBackgroundImage = backgroundImage;
  bakedZoom.value = 1;
  bakedViewRect = null;
  imageCache.clear();

  syncFallbackSize(backgroundImage);
  syncInteractionSize(backgroundImage);
  await nextTick();
  await document.fonts.ready;
  // Wait for the browser to complete layout/paint after font swap before measuring positions.
  await new Promise<void>(r => requestAnimationFrame(() => requestAnimationFrame(() => r())));
  applyFitToViewport();
  await drawBackground(backgroundImage);

  const { state2, seatsOurs, colors, overrideColors, priceGroups } = getSeatState();
  await drawAllSeats(seatmapData, state2, seatsOurs, colors, overrideColors, priceGroups);

  canvasReady = true;
  scheduleBake();

  // Safety-net: re-check after layout settles in case wrapperTop shifted (font swap, late reflow).
  setTimeout(() => {
    if (!wrapperRef.value || !canvasFallback.value) return;
    const isMobile = window.innerWidth < 768;
    if (!isMobile) return;
    const currentTop = wrapperRef.value.getBoundingClientRect().top + window.scrollY;
    const viewportHeight = window.visualViewport?.height ?? window.innerHeight;
    const expected = viewportHeight - currentTop - 150;
    if (mobileLockedHeight != null && Math.abs(mobileLockedHeight - expected) > 5) {
      mobileLockedHeight = null;
      applyFitToViewport();
      scheduleBake();
    }
  }, 500);
}

onMounted(async () => {
  if (seatmapStore.loadedSeatmaps[props.name]) {
    await initCanvas();
  }
  window.addEventListener('resize', onResize);
  if (wrapperRef.value) {
    lastWrapperWidth = wrapperRef.value.clientWidth;
    lastWrapperHeight = wrapperRef.value.clientHeight;
    wrapperResizeObserver = new ResizeObserver(onWrapperResize);
    wrapperResizeObserver.observe(wrapperRef.value);
  }
});

// Re-initialize everything when the seatmap name prop changes (user navigated to a different seatmap).
watch(
  () => props.name,
  async () => {
    canvasReady = false;
    cachedBackgroundImage = null;
    imageCache.clear();
    bakedZoom.value = 1;
    bakedViewRect = null;
    highlightedSeatIndex = -1;
    stopBlink();
    clearHighlight();
    hideTooltip();
    zoom.value = 1;
    zoomMin.value = 1;
    panX.value = 0;
    panY.value = 0;
    mobileLockedHeight = null;
    const wrapper = wrapperRef.value;
    if (wrapper) wrapper.style.height = '';
    if (seatmapStore.loadedSeatmaps[props.name]) {
      await initCanvas();
    }
  }
);

// Initialize canvas once seatmap data finishes loading from the server.
watch(
  () => seatmapStore.loadedSeatmaps[props.name],
  async (loaded) => {
    if (loaded) await initCanvas();
  }
);

// Redraw seats and re-bake when seat reservation state changes (WebSocket update).
watch(
  () => seatmapStore.seatStates[props.name],
  async () => {
    if (!canvasReady) return;
    const seatmapData = getSeatmapData();
    if (!seatmapData) return;
    const { state2, seatsOurs, colors, overrideColors, priceGroups } = getSeatState();
    await drawAllSeats(seatmapData, state2, seatsOurs, colors, overrideColors, priceGroups);
    bakedViewRect = null;
    scheduleBake();
  }
);

onBeforeUnmount(() => {
  stopBlink();
  if (bakeTimeout !== null) clearTimeout(bakeTimeout);
  window.removeEventListener('resize', onResize);
  if (resizeTimeout !== null) clearTimeout(resizeTimeout);
  if (wrapperResizeObserver) {
    wrapperResizeObserver.disconnect();
    wrapperResizeObserver = null;
  }
  imageCache.clear();
});
</script>
